import { NgClass } from '@angular/common';
import { Component, DestroyRef, DOCUMENT, ElementRef, inject, NgZone, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { filter, map, of, switchMap, timer } from 'rxjs';

import { HelperService } from './helper.service';
import { startNoBrowserAutofill } from './no-browser-autofill';

/** How long a page change may take before the loader is shown for it. */
export const ROUTE_BUSY_DELAY_MS = 250;

/** Root component: the router outlet and the app-wide loading spinner. */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, NgClass]
})
export class AppComponent implements OnDestroy {
    helper = inject(HelperService);
    /** True while a page change is taking noticeable time (the next page's code or data is still on its way). */
    routeBusy = false;

    private readonly stopNoAutofill: () => void;

    constructor() {
        const zone = inject(NgZone);
        const doc = inject<Document>(DOCUMENT);
        const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

        // Outside Angular's zone: the observer fires on every DOM change and must never start change detection.
        this.stopNoAutofill = zone.runOutsideAngular(() => startNoBrowserAutofill(doc, host));

        /*
         * ⚠ THE LOADER USED TO SWITCH OFF THE MOMENT AN API CALL ANSWERED, and the next screen's code was still downloading after that, so a
         * sign-in sat on the old screen with nothing to say it was working (owner, 2026-10-27). A page change that lasts longer than
         * ROUTE_BUSY_DELAY_MS now keeps the same loader on until it ends; a fast one never flashes it.
         */
        const isEnd = (event: unknown): boolean => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError;
        inject(Router)
            .events.pipe(
                filter((event) => event instanceof NavigationStart || isEnd(event)),
                switchMap((event) => (event instanceof NavigationStart ? timer(ROUTE_BUSY_DELAY_MS).pipe(map(() => true)) : of(false))),
                takeUntilDestroyed(inject(DestroyRef)),
            )
            .subscribe((busy) => (this.routeBusy = busy));
    }

    ngOnDestroy(): void {
        this.stopNoAutofill();
    }
}
